import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Hotel, FileSpreadsheet, Keyboard, CheckCircle2, Clock, ArrowRight } from 'lucide-react';
import { MarketingPageShell } from '@/components/marketing/MarketingPageShell';
import { PRIMARY_SIGNUP_CTA } from '@/lib/marketing-cta';
import { morphSpringSoft } from '@/lib/motion';
import quickbooksLogo from '@/assets/quickbooks-logo.png';

const pmsIntegrations = [
  {
    name: 'Mews',
    status: 'available' as const,
    description:
      'Connect Mews to sync daily reservations, occupancy, ADR, RevPAR, and room revenue automatically.',
  },
  {
    name: 'Cloudbeds',
    status: 'available' as const,
    description: 'Connect Cloudbeds to sync reservations, daily metrics, and channel revenue automatically.',
  },
  {
    name: 'Oracle Opera',
    status: 'roadmap' as const,
    description: 'Enterprise PMS support is on the roadmap for multi-property and branded-hotel stacks.',
  },
  {
    name: 'CSV / manual',
    status: 'available' as const,
    description: 'Import daily metrics from any PMS export or enter KPIs manually while onboarding.',
  },
];

const accountingIntegrations = [
  {
    name: 'QuickBooks Online',
    logo: quickbooksLogo,
    status: 'available' as const,
    description: 'Sync expenses and P&L data alongside your hotel operating metrics.',
  },
];

export default function IntegrationsPublic() {
  useEffect(() => {
    document.title = 'PMS & data integrations · Vesta CFO';
  }, []);

  return (
    <MarketingPageShell navActive="integrations">
      <div className="relative overflow-hidden bg-white">
        <section className="relative max-w-6xl mx-auto px-6 pt-12 sm:pt-16 pb-10">
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={morphSpringSoft}
            className="font-serif text-4xl sm:text-5xl text-vesta-navy font-normal leading-tight max-w-3xl"
          >
            Connect your PMS and financial stack
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...morphSpringSoft, delay: 0.06 }}
            className="mt-5 text-lg text-vesta-navy/80 max-w-2xl leading-relaxed"
          >
            Vesta CFO pulls hotel-native metrics from your property systems — RevPAR, ADR, GOPPAR, occupancy, and channel
            mix — without reconciling ten exports. See what we support today and what&apos;s coming next.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...morphSpringSoft, delay: 0.12 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <Link
              to="/auth"
              className="inline-flex items-center gap-2 rounded-xl bg-vesta-gold hover:bg-vesta-gold/90 text-vesta-navy font-semibold px-6 py-3.5 shadow-md transition-colors"
            >
              {PRIMARY_SIGNUP_CTA}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/how-to-get-started"
              className="inline-flex items-center gap-2 rounded-xl border-2 border-vesta-navy/15 text-vesta-navy font-medium px-6 py-3.5 hover:border-vesta-gold/50 transition-colors"
            >
              Getting started
            </Link>
          </motion.div>
        </section>

        <section className="max-w-6xl mx-auto px-6 pb-12">
          <h2 className="font-serif text-2xl text-vesta-navy mb-2 flex items-center gap-2">
            <Hotel className="w-6 h-6 text-vesta-gold" />
            Property management systems
          </h2>
          <p className="text-vesta-navy/75 text-sm mb-6 max-w-2xl">
            Your PMS is the primary source for daily hotel KPIs. Starter includes one PMS connection; Growth supports
            multiple properties and systems.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {pmsIntegrations.map((item, i) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...morphSpringSoft, delay: i * 0.04 }}
                className="rounded-2xl border border-vesta-navy/10 bg-white p-6 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h3 className="font-semibold text-vesta-navy">{item.name}</h3>
                  {item.status === 'available' ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
                      <CheckCircle2 className="w-3 h-3" />
                      Available
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-vesta-navy/70 bg-vesta-mist px-2 py-1 rounded-full">
                      <Clock className="w-3 h-3" />
                      Roadmap
                    </span>
                  )}
                </div>
                <p className="text-sm text-vesta-navy/80 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 pb-12">
          <h2 className="font-serif text-2xl text-vesta-navy mb-6">Accounting &amp; manual options</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {accountingIntegrations.map((item) => (
              <div
                key={item.name}
                className="rounded-2xl border border-vesta-navy/10 bg-white p-6 shadow-sm flex flex-col"
              >
                <img src={item.logo} alt="" className="h-8 w-auto mb-4 object-contain object-left" />
                <h3 className="font-semibold text-vesta-navy mb-2">{item.name}</h3>
                <p className="text-sm text-vesta-navy/80 flex-1">{item.description}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                  <CheckCircle2 className="w-3 h-3" />
                  Available after signup
                </span>
              </div>
            ))}
            <Link
              to="/auth"
              className="rounded-2xl border border-vesta-navy/10 bg-vesta-mist/30 p-6 hover:border-vesta-gold/40 transition-colors group"
            >
              <FileSpreadsheet className="w-8 h-8 text-vesta-navy mb-3" />
              <h3 className="font-semibold text-vesta-navy mb-2 group-hover:text-vesta-gold transition-colors">
                Upload CSV
              </h3>
              <p className="text-sm text-vesta-navy/80">Any PMS export — daily metrics, expenses, or channel revenue.</p>
            </Link>
            <Link
              to="/auth"
              className="rounded-2xl border border-vesta-navy/10 bg-vesta-mist/30 p-6 hover:border-vesta-gold/40 transition-colors group"
            >
              <Keyboard className="w-8 h-8 text-vesta-navy mb-3" />
              <h3 className="font-semibold text-vesta-navy mb-2 group-hover:text-vesta-gold transition-colors">
                Manual entry
              </h3>
              <p className="text-sm text-vesta-navy/80">Enter RevPAR, ADR, and occupancy while your PMS connection is pending.</p>
            </Link>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 pb-16">
          <div className="rounded-3xl border border-vesta-navy/10 bg-gradient-to-br from-vesta-mist/50 to-white p-8 sm:p-10 text-center">
            <p className="text-vesta-navy/80 max-w-lg mx-auto mb-6">
              After you create an account, connect integrations from your workspace — OAuth for Mews and QuickBooks,
              or upload CSVs in minutes.
            </p>
            <Link
              to="/auth"
              className="inline-flex items-center gap-2 rounded-xl bg-vesta-navy hover:bg-vesta-navy-muted text-white font-semibold px-8 py-3.5 transition-colors"
            >
              {PRIMARY_SIGNUP_CTA}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </MarketingPageShell>
  );
}
